
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Bot, Play, Pause, CheckCircle, AlertCircle, Loader2, Database, Settings } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useWorkflowExecution } from '@/hooks/useWorkflowExecution';
import { useWorkflowBlueprint } from '@/hooks/useWorkflowBlueprint';
import { WorkflowRunHistory } from '@/components/workflow/WorkflowRunHistory';
import { WorkflowRunInspector } from '@/components/workflow/WorkflowRunInspector';
import { WorkflowSetupWizard } from '@/components/workflow/setup/WorkflowSetupWizard';

export const AIWorkflowController = () => {
  const {
    isRunning,
    currentStep,
    steps,
    currentRunId,
    executeWorkflow,
    replayWorkflow,
    rollbackToRun,
  } = useWorkflowExecution();

  const { blueprint, isLoading: blueprintLoading, isSetupComplete } = useWorkflowBlueprint();

  const [workflowConfig, setWorkflowConfig] = useState<any>(null);
  const [inspectingRunId, setInspectingRunId] = useState<string | null>(null);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    loadWorkflowConfig();
  }, []);

  // Auto-open inspector when workflow completes
  useEffect(() => {
    if (currentRunId && !isRunning) {
      setInspectingRunId(currentRunId);
    }
  }, [currentRunId, isRunning]);

  const loadWorkflowConfig = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: crmIntegrations } = await supabase
        .from('crm_integrations')
        .select('*')
        .eq('status', 'active');

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      setWorkflowConfig({
        crmIntegrations: crmIntegrations || [],
        userProfile: profile,
      });
    } catch (error) {
      console.error('Error loading workflow config:', error);
    }
  };

  const handleStart = () => {
    executeWorkflow(workflowConfig?.crmIntegrations || [], workflowConfig?.userProfile);
  };

  const handleReplay = (runId: string) => {
    replayWorkflow(runId, workflowConfig?.crmIntegrations || [], workflowConfig?.userProfile);
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'running': return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      case 'error': return <AlertCircle className="h-4 w-4 text-destructive" />;
      default: return <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />;
    }
  };

  const overallProgress = steps.filter(s => s.status === 'completed').length / steps.length * 100;
  const hasFailed = steps.some(s => s.status === 'error');
  const lastCompletedStep = steps.filter(s => s.status === 'completed').length;

  const getWorkflowState = () => {
    if (isRunning) return 'running';
    if (hasFailed) return 'failed';
    if (lastCompletedStep === steps.length) return 'completed';
    return 'idle';
  };

  const stateLabel: Record<string, string> = { idle: 'Idle', running: 'Running', completed: 'Completed', failed: 'Failed' };
  const stateVariant: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    idle: 'secondary', running: 'default', completed: 'outline', failed: 'destructive',
  };
  const workflowState = getWorkflowState();

  // Loading state
  if (blueprintLoading) {
    return (
      <Card className="p-6 flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading workflow configuration…</p>
      </Card>
    );
  }

  // Gate: show setup wizard if blueprint is not complete
  if (!isSetupComplete || showSetup) {
    return (
      <div className="space-y-6">
        <WorkflowSetupWizard onComplete={() => setShowSetup(false)} />
      </div>
    );
  }

  // If inspecting a run, show the inspector
  if (inspectingRunId && !isRunning) {
    return (
      <div className="space-y-6">
        <WorkflowRunInspector runId={inspectingRunId} onBack={() => setInspectingRunId(null)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <Bot className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-semibold">AI Workflow Controller</h2>
          <Badge variant={stateVariant[workflowState]}>{stateLabel[workflowState]}</Badge>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setShowSetup(true)}>
            <Settings className="h-4 w-4 mr-1" /> Edit Blueprint
          </Button>
        </div>

        {/* Blueprint summary */}
        {blueprint && (
          <div className="flex flex-wrap gap-2 mb-4 p-3 bg-muted/50 rounded-lg">
            <Badge variant="outline">{blueprint.industry}</Badge>
            <Badge variant="outline">{blueprint.data_source_type === 'crm' ? 'CRM' : 'File Upload'}</Badge>
            <Badge variant="outline">{(blueprint.prediction_goals as string[])?.length || 0} predictions</Badge>
            <Badge variant="outline">{(blueprint.dashboard_config as any)?.type || 'default'} dashboard</Badge>
          </div>
        )}

        {!workflowConfig?.crmIntegrations?.length && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No CRM integrations found. Please connect a CRM system first to enable automated workflows.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <h3 className="font-medium">Automated Data Pipeline</h3>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              CRM → <Database className="h-3 w-3 inline" /> In-App Table → AI Cleaning → Predictions → Dashboard
            </p>
          </div>

          <div className="flex items-center gap-2 text-sm bg-muted/50 p-3 rounded-lg">
            <Database className="h-4 w-4 text-primary" />
            <span className="font-medium">Output:</span>
            <span className="text-muted-foreground">In-App Data Table (persistent, version-tracked)</span>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={handleStart} disabled={isRunning || !workflowConfig?.crmIntegrations?.length} className="flex items-center gap-2">
              <Play className="h-4 w-4" /> Start Workflow
            </Button>
            {isRunning && (
              <Button onClick={() => toast.info('Workflow will complete current step')} variant="outline">
                <Pause className="h-4 w-4" /> Stop
              </Button>
            )}
          </div>

          {isRunning && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Overall Progress</span>
                <span>{Math.round(overallProgress)}%</span>
              </div>
              <Progress value={overallProgress} className="h-2" />
            </div>
          )}

          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={step.id} className={`flex items-center gap-3 p-3 rounded-lg border ${index === currentStep && isRunning ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'}`}>
                {getStepIcon(step.status)}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{step.name}</span>
                    {step.status === 'running' && <span className="text-sm text-muted-foreground">{step.progress}%</span>}
                  </div>
                  {step.result && <p className="text-sm text-green-600">{step.result}</p>}
                  {step.error && <p className="text-sm text-destructive">{step.error}</p>}
                </div>
              </div>
            ))}
          </div>

          {workflowConfig?.crmIntegrations?.length > 0 && (
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-2">Connected CRM Systems</h4>
              <div className="flex flex-wrap gap-2">
                {workflowConfig.crmIntegrations.map((crm: any) => (
                  <Badge key={crm.id} variant="outline">{crm.name} ({crm.crm_type})</Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      <WorkflowRunHistory
        onSelectRun={setInspectingRunId}
        onReplay={handleReplay}
        onRollback={rollbackToRun}
        selectedRunId={inspectingRunId}
        isRunning={isRunning}
      />
    </div>
  );
};
